import { Empty, Select, Switch } from 'antd'
import React, { useEffect, useMemo } from 'react'
import {
  CenteredContainer,
  DataLabel,
  RenderDiv,
  StyledSpan,
  StyledTable,
  TextArea,
} from './style'

// Turn a raw sub-topic param key (e.g. "readingAbilityScore") into a readable
// label ("Reading Ability Score"). Mirrors the candidate table's
// formatSubTopicLabel so the filter and the columns read the same. The label
// is display-only — the raw `topic` key still drives the filter payload.
// CEFR is an ordinal scale (A1 < A2 < B1 < B2 < C1 < C2), not a number — the
// filter sends the level as-is and the server compares by rank.
const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']

const formatSubTopicLabel = key => {
  if (!key || typeof key !== 'string') return key
  if (key === 'cefrLevel') return 'CEFR Level'
  if (key === 'englishVerbalScore') return 'Verbal Score'
  if (key === 'overallScore') return 'Overall Score'
  // Only split camelCase; leave user-defined names (e.g. custom-assessment
  // section titles like "Logical Ability") untouched.
  if (!/\s/.test(key) && /[A-Z]/.test(key)) {
    return key.replace(/([A-Z])/g, ' $1').trim()
  }
  return key
}

const RoundsFilter = ({
  filterOptions,
  filterValues,
  handleFilterChange,
  mode,
  setMode,
  data,
  setData,
}) => {
  // Derive table rows directly from filterOptions.roundScoreList so the
  // panel always reflects whatever the score/list API returned for the
  // current round. The parent's `data` state is still the source of truth
  // for edits — we seed it from `rows` on mount / whenever roundScoreList
  // changes, but fall back to `rows` on first paint so AI-Interview-style
  // rounds (where score/list returns a single overallScore subtopic with
  // no prior sheet data) don't render "No Data Found" before the parent
  // state catches up.
  const rows = useMemo(() => {
    const list = filterOptions?.roundScoreList
    if (!Array.isArray(list) || list.length === 0) return []

    const selectedValuesMap = (filterValues?.selectedRoundScore || []).reduce(
      (map, item) => {
        if (item?.topic) {
          map[item.topic] = item?.value ?? ''
        }
        return map
      },
      {}
    )

    return list.reduce((acc, item, index) => {
      acc.push({
        key: item.topic.name,
        topic: item.topic.name,
        label: item.topic.name,
        value: selectedValuesMap[item?.topic?.name] ?? '',
        isTopic: true,
        index: index.toString(),
        max: item.topic.maxScore,
      })

      const subTopics = Array.isArray(item.subTopics) ? item.subTopics : []
      subTopics.forEach((subTopic, i) => {
        const key = subTopic.param
        acc.push({
          key,
          topic: key,
          label: formatSubTopicLabel(key),
          value: selectedValuesMap[key] ?? '',
          isTopic: false,
          index: `${index},${i}`,
          max: item.topic.maxScore,
        })
      })

      return acc
    }, [])
  }, [filterOptions?.roundScoreList, filterValues?.selectedRoundScore])

  useEffect(() => {
    if (rows.length > 0) {
      setData(rows)
    }
  }, [rows])

  const displayData = data?.length > 0 ? data : rows

  const onChangeScore = (value, record) => {
    // cefrLevel is an ordinal string (A1..C2), not a number — never run it
    // through the numeric validation.
    const isCefr = record.key === 'cefrLevel'
    const numericValue = parseFloat(value)
    const numericRegex = /^\d*\.?\d*$/

    const isError =
      !isCefr &&
      value.trim() !== '' &&
      (!numericRegex.test(value) || numericValue > record.max)

    const updatedData = displayData.map(item => {
      if (item.key === record.key) {
        return { ...item, value, error: isError }
      }
      return item
    })
    setData(updatedData)

    handleFilterChange(updatedData, 'selectedRoundScore')
  }

  return (
    <>
      {displayData.length === 0 ? (
        <CenteredContainer>
          <Empty description="No Data Found" />
        </CenteredContainer>
      ) : (
        <>
          <div style={{ margin: '1.5em', display: 'flex', gap: '5%' }}>
            <Switch onChange={(e) => handleFilterChange(e,'selectedRoundMode')} checked={filterValues?.selectedRoundMode} />
            {mode ? 'Selection Mode' : 'Rejection Mode'}
          </div>

          <StyledTable dataSource={displayData} pagination={false} rowKey="key">
            <StyledTable.Column
              title=""
              width={250}
              dataIndex="topic"
              render={(text, record) => (
                <DataLabel color={record?.isTopic} weight="600">
                  {record?.label ?? text}
                </DataLabel>
              )}
            />
            <StyledTable.Column
              title="PASSING SCORE"
              dataIndex="value"
              render={(text, record) => (
                <>
                  <RenderDiv>
                    <StyledSpan>
                      {mode ? '≥' : '≤'}
                    </StyledSpan>
                    {record?.key === 'cefrLevel' ? (
                      <Select
                        allowClear
                        placeholder="CEFR"
                        style={{ width: '100%' }}
                        value={record.value || undefined}
                        options={CEFR_LEVELS.map(level => ({
                          label: level,
                          value: level,
                        }))}
                        onChange={value =>
                          onChangeScore(value ?? '', record)
                        }
                      />
                    ) : (
                      <TextArea
                        color={record?.isTopic}
                        placeholder={record?.max?.toString()}
                        value={record.value}
                        backgroundColor={record?.isTopic}
                        onChange={e => onChangeScore(e.target.value, record)}
                      />
                    )}
                  </RenderDiv>
                  {record?.error && (
                    <div style={{ color: 'red', marginLeft: '3em' }}>
                      Enter valid number
                    </div>
                  )}
                </>
              )}
            />
          </StyledTable>
        </>
      )}
    </>
  )
}

export default RoundsFilter
